import { Link } from "@tanstack/react-router";
import { SignupOrTerminalLink } from "@/components/site/SignupOrTerminalLink";
import { LandingArticleSlider } from "@/components/site/LandingArticleSlider";
import {
  AlertTriangle,
  ArrowRight,
  BellRing,
  CalendarRange,
  CandlestickChart,
  Check,
  CircleSlash2,
  Clock3,
  FileCheck2,
  Gauge,
  GraduationCap,
  LineChart,
  LockKeyhole,
  Radar,
  ScanSearch,
  ShieldCheck,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MonthlyPerformanceTrajectory } from "@/components/site/MonthlyPerformanceTrajectory";

const recordStats = [
  { value: "241", label: "Closed trades" },
  { value: "+95.94R", label: "Net result" },
  { value: "1.72", label: "Profit factor" },
  { value: "8.25R", label: "Maximum drawdown" },
];

const trustFailures = [
  "Wins promoted while losing calls disappear",
  "Entry zones with unclear invalidation",
  "High-frequency calls designed to keep a channel busy",
  "Profit screenshots without a complete trade ledger",
];

const signalFields = [
  { label: "Direction", value: "BUY / LONG", icon: Radar },
  { label: "Entry protocol", value: "Defined execution zone", icon: Target },
  { label: "Invalidation", value: "Hard protective stop", icon: ShieldCheck },
  { label: "Objectives", value: "Managed target sequence", icon: LineChart },
];

const processSteps = [
  {
    number: "01",
    title: "Wait for qualification",
    text: "The desk rejects weak conditions. No trade is a valid operational decision.",
  },
  {
    number: "02",
    title: "Receive the execution plan",
    text: "A qualified decision carries direction, entry, invalidation, objectives, and risk context.",
  },
  {
    number: "03",
    title: "Track the same record",
    text: "Closed decisions remain in the record, including losses. Outcomes are not selected for promotion.",
  },
];

const freeSuite = [
  {
    icon: ScanSearch,
    featured: true,
    title: "StructureLens: institutional Smart Money analysis",
    what: "A complete institutional reading of any gold chart under the Smart Money Concepts framework. Scan the live Orion chart in one click or upload your own screenshot, and receive a structured desk report.",
    points: [
      "Multi-timeframe bias and market structure (BOS, CHoCH, MSS)",
      "Buy-side and sell-side liquidity, sweeps, and inducement",
      "Displacement and Fair Value Gap qualification",
      "Order Blocks, Breakers, and premium or discount arrays",
      "Session context and risk-to-reward assessment",
      "Concerns, confidence, and an honest neutral verdict when warranted",
    ],
    impact: "An objective second opinion on every idea before you risk capital, read the way institutional desks read liquidity, not through retail indicators.",
  },
  {
    icon: CalendarRange,
    title: "Historical Seasonality",
    what: "Fifteen years of XAUUSD monthly performance, with 1, 5, and 10 year views, positive-month frequency, and every month of the year available to inspect.",
    impact: "Know whether gold has historically leaned positive or negative in the current month before you take a directional view.",
  },
  {
    icon: Gauge,
    title: "Daily Range and ADR",
    what: "Today's range compared with the 14 day average daily range, including the percentage of the typical move already consumed.",
    impact: "Avoid chasing price after the day's normal range is spent, and set targets that respect realistic volatility.",
  },
  {
    icon: Clock3,
    title: "Session Clock",
    what: "Live Asian, London, and New York session tracking with countdowns to each open and close, plus weekend market status.",
    impact: "Focus on high-liquidity windows and step aside during dead liquidity conditions.",
  },
  {
    icon: CandlestickChart,
    title: "Live XAUUSD Chart",
    what: "A clean proprietary gold chart from 5 minute to daily, with market climate and desk levels, free of broker noise.",
    impact: "Follow gold in a calm, data-dense environment built for decisions, not distraction.",
  },
  {
    icon: GraduationCap,
    title: "Gold Academy",
    what: "More than 20 structured lessons on liquidity, market structure, order blocks, sessions, and risk, each with gold-specific diagrams.",
    impact: "Move from pattern guessing to understanding how liquidity actually moves the gold market.",
  },
];

const tiers = [
  {
    label: "No card required",
    name: "Free Member",
    featured: false,
    items: [
      "Selected Grade A signals with full entry, stop, and targets",
      "Weekly StructureLens institutional Smart Money analysis",
      "Historical Seasonality across 1, 5, and 10 years",
      "Daily range, ADR, and live session clock",
      "Live XAUUSD chart from 5 minute to daily",
      "Gold Academy: structured lessons on market structure and risk",
      "Complete trade ledger and performance record",
      "Priority notice when desk enrolment opens",
    ],
  },
  {
    label: "Paid access, opening soon",
    name: "Desk Subscriber",
    featured: true,
    items: [
      "Every published signal, including all A+ decisions",
      "Instant browser alerts on entries and trade updates",
      "Full XAU Terminal™ execution access",
      "Expanded StructureLens Smart Money analyses",
      "Everything included in the free account",
    ],
  },
];

const gradeRows = [
  { measure: "Win rate", all: "38.8%", aplus: "45.0%" },
  { measure: "Profit factor", all: "1.31", aplus: "1.93" },
  { measure: "Maximum drawdown", all: "16.50R", aplus: "5.00R" },
  { measure: "Second target reached", all: "23.9%", aplus: "35.0%" },
];

const questions = [
  {
    question: "Do free members receive live signals?",
    answer:
      "Yes. Free members receive selected Grade A signals with full entry, stop, and targets, plus Academy lessons and the live chart. Desk subscribers receive every published signal, including all A+ decisions, with instant browser alerts.",
  },
  {
    question: "Which tools are free without a subscription?",
    answer:
      "The free account includes a weekly StructureLens institutional analysis under Smart Money Concepts, Historical Seasonality, the daily range and ADR reading, the live session clock, the live XAUUSD chart, the full Gold Academy, and the complete performance record. No card is required.",
  },
  {
    question: "What does StructureLens analyse?",
    answer:
      "StructureLens delivers a full institutional reading under the Smart Money Concepts framework: higher-timeframe bias, market structure (BOS, CHoCH, MSS), liquidity pools and sweeps, displacement, Fair Value Gaps, Order Blocks, premium and discount positioning, session context, risk, concerns, and confidence. It can return a neutral verdict when conditions do not qualify.",
  },
  {
    question: "Are these Telegram gold signals?",
    answer:
      "No. Signals and execution updates are delivered inside the XAU Pro Academy XAU Terminal™ with browser alerts. The record stays connected to the platform rather than a disposable message channel.",
  },
  {
    question: "How many XAUUSD signals are published each day?",
    answer:
      "There is no daily quota. Orion can publish a qualified decision or remain neutral. The service prioritizes capital preservation over signal frequency.",
  },
  {
    question: "Do you guarantee profitable gold signals?",
    answer:
      "No. Gold trading involves loss risk and no legitimate signal provider can guarantee profit. The published record includes losing periods and drawdown.",
  },
  {
    question: "Can I inspect past results before paying?",
    answer:
      "Yes. The public Performance Record reports every included closed trade from 2024 through October 2026, and the Case Studies archive provides candle-by-candle replays of selected desk decisions.",
  },
  {
    question: "When will paid desk access open?",
    answer:
      "Paid access will open in a limited cohort when enrolment and payment operations are ready. Joining the free account now keeps you eligible for the opening notice without requiring a card.",
  },
  {
    question: "What makes a gold signal provider trustworthy?",
    answer:
      "A complete closed-trade record that includes losses, a defined stop loss on every signal, results reported in risk units with drawdown, a stated session and news policy, and no guaranteed profit claims.",
  },
  {
    question: "What is a realistic win rate for gold signals?",
    answer:
      "Durable gold strategies often win less than half of their trades and stay profitable through reward that exceeds risk. The XAU Pro record shows a 44.8% win rate with a profit factor of 1.72. Claims above 90% without published drawdown should be treated as a warning sign.",
  },
  {
    question: "Can I use gold signals on a prop firm evaluation?",
    answer:
      "Only if every signal carries a defined stop and you size positions to the daily and maximum drawdown limits of your firm. Fixed risk per trade and avoidance of unstable news windows matter more than signal frequency.",
  },
];

export const signalsLandingQuestions = questions;

const dueDiligence = [
  { title: "A complete record, losses included", body: "Every closed trade should remain visible, with dates, levels, and outcome. A feed of winning screenshots is marketing, not evidence." },
  { title: "Results in risk units with drawdown", body: "Pips and percentage claims hide position size. Results expressed in R, alongside maximum drawdown, show what a trader actually endured." },
  { title: "A defined stop on every signal", body: "A signal without an invalidation level cannot be sized. It transfers unlimited risk to the follower." },
  { title: "A realistic win rate", body: "Claims of 90% or more usually rely on deleted losses or distant stops. Sustainable gold execution relies on reward exceeding risk." },
  { title: "Session and news discipline", body: "Gold moves on London, New York, and high-impact data. A provider should state when it trades and when it stands aside." },
  { title: "Compatibility with funded accounts", body: "Fixed risk and logical stops let a trader respect daily and maximum drawdown limits. Random frequency breaches them." },
];

const comparisonRows: [string, string, string][] = [
  ["Delivery", "Unmoderated chat channel", "Dedicated XAU Terminal™ with browser alerts"],
  ["Record", "Editable, losses often removed", "Published closed-trade record since January 2024"],
  ["Measurement", "Pips without position size", "R multiples with maximum drawdown"],
  ["Risk definition", "Stop missing or moved", "Entry, stop, and targets on every decision"],
  ["Frequency", "Daily quota, around the clock", "No quota, neutral is an accepted outcome"],
  ["News conditions", "Trades through releases", "Defined news freeze and macro protection"],
];

export function SignalsLandingDraft({ variant = "framed" }: { variant?: "framed" | "page" }) {
  return (
    <div
      className={
        variant === "page"
          ? "min-w-0 overflow-hidden bg-background"
          : "min-w-0 overflow-hidden rounded-lg border border-border/60 bg-background shadow-elevated"
      }
    >
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero px-5 pb-16 pt-14 sm:px-8 lg:px-14 lg:pb-20 lg:pt-20">
        <div className="grid-bg absolute inset-0 opacity-25" />
        <div className="relative mx-auto max-w-5xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
            XAUUSD Signals · Structured Gold Execution
          </div>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-bold leading-[1.08] sm:text-5xl lg:text-6xl">
            Gold signals you can verify before you trust.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            For traders tired of deleted losses, impossible win-rate claims, and Telegram calls with no accountable record. Orion delivers selective XAUUSD decisions with defined risk and a published performance history.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild variant="gold" size="xl">
              <SignupOrTerminalLink>
                Create Free Account <ArrowRight className="h-4 w-4" />
              </SignupOrTerminalLink>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/track-record">Inspect the Full Record</Link>
            </Button>
          </div>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            <span className="inline-flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald" /> No card required</span>
            <span className="inline-flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald" /> Losses remain visible</span>
            <span className="inline-flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald" /> Browser alerts</span>
          </div>
        </div>
      </section>

      <section className="border-b border-border/60 bg-card/35 px-5 py-8 sm:px-8 lg:px-14">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-md border border-border/60 bg-border/50 lg:grid-cols-4">
          {recordStats.map((stat) => (
            <div key={stat.label} className="bg-background/85 px-4 py-5">
              <div className="font-mono text-2xl font-semibold tabular-nums text-gold sm:text-3xl">{stat.value}</div>
              <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-3 max-w-5xl text-xs leading-relaxed text-muted-foreground">
          Recorded from 1 January 2024 to 31 October 2026. Historical results do not guarantee future performance. One risk unit, R, expresses result relative to the defined risk on each trade.
        </p>
      </section>

      <section className="border-b border-border/60 px-5 py-14 sm:px-8 lg:px-14 lg:py-20">
        <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-destructive">The trust problem</div>
            <h2 className="mt-3 font-display text-3xl font-semibold">A winning screenshot is not a track record.</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              If you have tried free or paid gold signals before, you already know what creates doubt. The problem is not finding another call. It is knowing whether the provider records the call when it loses.
            </p>
          </div>
          <div className="divide-y divide-border/60 border-y border-border/60">
            {trustFailures.map((failure) => (
              <div key={failure} className="flex gap-3 py-4 text-sm text-foreground/85">
                <CircleSlash2 className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <span>{failure}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border/60 bg-card/25 px-5 py-14 sm:px-8 lg:px-14 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-3xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Due diligence framework</div>
            <h2 className="mt-3 font-display text-3xl font-semibold">How to choose the best gold signal provider: five checks.</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Apply these checks to any XAUUSD signal service, including this one. A provider that fails one of them should not receive your capital or your evaluation account.
            </p>
          </div>
          <ol className="mt-10 grid gap-px overflow-hidden rounded-md border border-border/60 bg-border/50 md:grid-cols-2">
            {dueDiligence.map((item, i) => (
              <li key={item.title} className="bg-background/85 p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">Check {i + 1}</div>
                <h3 className="mt-2 font-display text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
              </li>
            ))}
          </ol>

          <h3 className="mt-14 font-display text-2xl font-semibold">Retail signal channels vs a structured execution desk</h3>
          <div className="mt-6 overflow-x-auto rounded-md border border-border/60">
            <table className="w-full min-w-[620px] text-left text-sm">
              <thead className="bg-card/60 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Criterion</th>
                  <th className="px-4 py-3">Typical retail channel</th>
                  <th className="px-4 py-3 text-gold">XAU Pro desk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {comparisonRows.map((row) => (
                  <tr key={row[0]}>
                    <td className="px-4 py-3 font-medium">{row[0]}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <span className="inline-flex gap-2"><CircleSlash2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />{row[1]}</span>
                    </td>
                    <td className="px-4 py-3 text-foreground/90">
                      <span className="inline-flex gap-2"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald" />{row[2]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Using signals on a funded evaluation? Size every trade with the{" "}
            <Link to="/position-size-calculator" className="text-gold underline-offset-4 hover:underline">gold lot size calculator</Link>{" "}
            and review the <Link to="/best-prop-firms-for-gold" className="text-gold underline-offset-4 hover:underline">prop firm rules for gold</Link>.
          </p>
        </div>
      </section>


      <section className="border-b border-border/60 bg-slate-deep/45 px-5 py-14 sm:px-8 lg:px-14 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-3xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Anatomy of a desk decision</div>
            <h2 className="mt-3 font-display text-3xl font-semibold">The information required to execute. Not the proprietary engine behind it.</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Members receive a clear operating plan while Orion's internal qualification logic remains protected. The example below illustrates the delivery structure and is not a live call.
            </p>
          </div>
          <div className="mt-8 grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-md border border-gold/30 bg-card p-5 shadow-gold sm:p-7">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-5">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Illustrative output</div>
                  <div className="mt-1 font-display text-xl font-semibold">XAUUSD Execution Plan</div>
                </div>
                <span className="rounded-sm border border-gold/30 bg-gold-muted px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-gold">Qualified</span>
              </div>
              <div className="mt-5 grid gap-px overflow-hidden rounded-md border border-border/60 bg-border/50 sm:grid-cols-2">
                {signalFields.map((field) => (
                  <div key={field.label} className="bg-background/80 p-4">
                    <field.icon className="h-4 w-4 text-gold" />
                    <div className="mt-4 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{field.label}</div>
                    <div className="mt-1 text-sm font-medium">{field.value}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-start gap-3 rounded-md bg-muted/45 p-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <p className="text-xs leading-relaxed text-muted-foreground">Risk context and concerns accompany the decision. A setup can be invalidated, paused, or withheld when conditions deteriorate.</p>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { icon: BellRing, title: "Immediate alert", text: "A browser notification when a new desk decision is published." },
                { icon: Clock3, title: "Selective frequency", text: "No quota and no forced daily signal when conditions do not qualify." },
                { icon: LockKeyhole, title: "Protected methodology", text: "Execution outputs are visible. Proprietary filters and engine rules stay private." },
              ].map((item) => (
                <div key={item.title} className="rounded-md border border-border/60 bg-card/55 p-5">
                  <item.icon className="h-5 w-5 text-gold" />
                  <h3 className="mt-3 text-sm font-semibold">{item.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border/60 bg-card/25 px-5 py-14 sm:px-8 lg:px-14 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-3xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">The free intelligence suite</div>
            <h2 className="mt-3 font-display text-3xl font-semibold">Institutional tools you can use from day one. Free.</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Before any payment, the free account opens the XAU Terminal™: the same market context the desk reads every session, with a clear purpose for each tool.
            </p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {freeSuite.map((tool) => (
              <div key={tool.title} className={`rounded-md border p-5 sm:p-6 ${tool.featured ? "border-gold/40 bg-card shadow-gold md:col-span-2" : "border-border/60 bg-card/55"}`}>
                <div className="flex items-center gap-3">
                  <tool.icon className="h-5 w-5 shrink-0 text-gold" />
                  <h3 className="text-base font-semibold">{tool.title}</h3>
                  {tool.featured && (
                    <span className="ml-auto rounded-sm border border-gold/30 bg-gold-muted px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-gold">Smart Money Concepts</span>
                  )}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{tool.what}</p>
                {tool.points && (
                  <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                    {tool.points.map((p) => (
                      <li key={p} className="flex gap-2 text-xs text-foreground/85">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-4 border-t border-border/60 pt-3">
                  <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-emerald">Impact on your trading</div>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/85">{tool.impact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border/60 px-5 py-14 sm:px-8 lg:px-14 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-3xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">What the free account includes</div>
            <h2 className="mt-3 font-display text-3xl font-semibold">Yes, free members receive live signals.</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Every desk decision carries a quality grade. Free members receive selected Grade A signals with full entry, stop, and targets, so the service can be tested on real trades before any payment. Desk subscribers receive every signal, including all A+ decisions.
            </p>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {tiers.map((tier) => (
              <div key={tier.name} className={`rounded-md border p-5 sm:p-7 ${tier.featured ? "border-gold/40 bg-card shadow-gold" : "border-border/60 bg-card/55"}`}>
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{tier.label}</div>
                <h3 className="mt-1 font-display text-xl font-semibold">{tier.name}</h3>
                <ul className="mt-5 space-y-3">
                  {tier.items.map((item) => (
                    <li key={item} className="flex gap-3 text-sm text-foreground/85">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${tier.featured ? "text-gold" : "text-emerald"}`} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 max-w-3xl">
            <h3 className="font-display text-xl font-semibold">Grade A and A+ across three years</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Both grades pass the same structural qualification. A+ decisions meet a stricter positioning condition and have historically delivered steadier results with shallower drawdown. The qualification rules themselves remain private.
            </p>
          </div>
          <div className="mt-5 overflow-x-auto rounded-md border border-border/60">
            <table className="w-full min-w-[480px] text-sm">
              <thead className="bg-muted/40 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Measure</th>
                  <th className="px-4 py-3 text-right font-medium">All qualified signals</th>
                  <th className="px-4 py-3 text-right font-medium text-gold">A+ only</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60 tabular-nums">
                {gradeRows.map((row) => (
                  <tr key={row.measure}>
                    <td className="px-4 py-3 text-muted-foreground">{row.measure}</td>
                    <td className="px-4 py-3 text-right font-mono">{row.all}</td>
                    <td className="px-4 py-3 text-right font-mono text-gold">{row.aplus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            Measured on the three-year XAUUSD archive (September 2023 to September 2026) under identical execution rules. Historical results do not guarantee future performance.
          </p>
        </div>
      </section>

      <section className="border-b border-border/60 px-5 py-14 sm:px-8 lg:px-14 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald">Evidence before access</div>
              <h2 className="mt-3 font-display text-3xl font-semibold">Judge the full sequence, not the latest trade.</h2>
            </div>
            <Button asChild variant="outline" size="sm"><Link to="/case-studies">Open Case Studies</Link></Button>
          </div>
          <MonthlyPerformanceTrajectory compact />
          <div className="mt-4 flex items-start gap-3 text-xs leading-relaxed text-muted-foreground">
            <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald" />
            <p>The published record includes winning trades, losing trades, risk-free exits, monthly outcomes, and maximum drawdown. Case Studies add candle-by-candle replay for selected desk decisions.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-border/60 bg-card/25 px-5 py-14 sm:px-8 lg:px-14 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-2xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">How it works</div>
            <h2 className="mt-3 font-display text-3xl font-semibold">From market condition to accountable outcome.</h2>
          </div>
          <ol className="mt-8 grid gap-4 md:grid-cols-3">
            {processSteps.map((step) => (
              <li key={step.number} className="border-t border-gold/35 pt-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">{step.number}</div>
                <h3 className="mt-3 text-base font-semibold">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-border/60 px-5 py-14 sm:px-8 lg:px-14 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-3xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Due diligence</div>
            <h2 className="mt-3 font-display text-3xl font-semibold">Questions to ask before choosing any gold signal provider.</h2>
          </div>
          <div className="mt-8 divide-y divide-border/60 border-y border-border/60">
            {questions.map((item) => (
              <details key={item.question} className="group py-5">
                <summary className="cursor-pointer list-none pr-8 text-sm font-semibold marker:hidden">{item.question}</summary>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <LandingArticleSlider />

      <section className="bg-gradient-hero px-5 py-16 text-center sm:px-8 lg:px-14 lg:py-20">
        <div className="mx-auto max-w-3xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Limited opening cohort</div>
          <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl">Do not trust another promise. Inspect the record first.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Create a free account now and enter the platform before paid desk enrolment opens. Paid access will open only when the service is operationally ready.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild variant="gold" size="xl"><SignupOrTerminalLink>Create Free Account <ArrowRight className="h-4 w-4" /></SignupOrTerminalLink></Button>
            <Button asChild variant="outline" size="lg"><Link to="/track-record">Review Performance</Link></Button>
          </div>
          <p className="mt-5 text-xs leading-relaxed text-muted-foreground">Educational service only. Trading leveraged products involves substantial risk. No outcome is guaranteed.</p>
        </div>
      </section>
    </div>
  );
}